import { FC, useState } from 'react';
import { OAuthProviderType } from '../../types/account.types';

interface LinkProviderButtonProps {
  provider: OAuthProviderType;
}

const getProviderInfo = (type: OAuthProviderType) => {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  switch (type) {
    case 'GOOGLE':
      return {
        name: 'Google',
        color: 'bg-white hover:bg-gray-50 border-gray-300 text-gray-700',
        // Note: Linking uses the same OAuth endpoints but with a different mode parameter
        linkUrl: `${apiUrl}/api/auth/google/login?mode=link`,
      };
    case 'FACEBOOK':
      return {
        name: 'Facebook',
        color: 'bg-[#1877F2] hover:bg-[#166FE5] border-[#1877F2] text-white',
        linkUrl: `${apiUrl}/api/auth/facebook/login?mode=link`,
      };
    default:
      return { name: type, color: 'bg-gray-500', linkUrl: '#' };
  }
};

// Provider-specific icons
function ProviderIcon({ provider }: { provider: OAuthProviderType }) {
  if (provider === 'GOOGLE') {
    return (
      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    );
  }

  if (provider === 'FACEBOOK') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    );
  }

  return null;
}

export const LinkProviderButton: FC<LinkProviderButtonProps> = ({ provider }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const info = getProviderInfo(provider);

  const handleLink = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const endpoint =
        provider === 'GOOGLE' ? '/api/auth/google/login' : '/api/auth/facebook/login';

      const response = await fetch(`${apiUrl}${endpoint}?mode=link`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to initiate ${info.name} linking`);
      }

      const data = await response.json();

      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authentication URL received');
      }
    } catch (err) {
      console.error(`${info.name} link error:`, err);
      setError(err instanceof Error ? err.message : 'Linking failed');
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleLink}
        disabled={loading}
        className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${info.color}`}
      >
        <div className="flex items-center gap-3">
          {loading ? (
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <ProviderIcon provider={provider} />
          )}
          <span className="font-medium">Link {info.name} Account</span>
        </div>
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>
      </button>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};
